"use client"

import { useState } from "react"
import * as St from "./index.styled"
import { CustomNumberUtils } from "../../../../../utils/custom-number-utils"
import { v4 as uuidv4 } from "uuid"
import { CustomDialog } from "../../../../../components/dialog/v1/CustomDialog"
import CustomBlockButton from "../../../../../components/button/CustomBlockButton"
import { customToast } from "../../../../../components/toast/custom-react-toastify/v1"
import { ProductOptionPickerModal } from "../../../../../components/product-option-picker-modal"
import { CustomQuantityKeypadModal } from "../../../../../components/custom-quantity-keypad-modal"
import { useModalBackClose } from "../../../../../hooks/useModalBackClose"

export function WegetSettingRackLocationModal({
    open,
    onClose,
    rackLocations,
    targetRackLocation,
    onSaveTargetRackLocation,
    onDeleteRackLocation,
}) {
    // 뒤로가기(popstate)로 설정 모달 닫기
    useModalBackClose(open, onClose)

    const [newRackLocation, setNewRackLocation] = useState(targetRackLocation)
    const [textareaForAdd, setTextareaForAdd] = useState("")
    // 제품 추가 방식 탭: 'PRODUCT'(셀러툴 불러오기, 기본) | 'TEXT'(텍스트 추가)
    const [addTab, setAddTab] = useState("PRODUCT")
    const [linkToLocationModalOpen, setLinkToLocationModalOpen] = useState(false)
    const [deleteModalOpen, setDeleteModalOpen] = useState(false)
    // 빠른채움: 상품 선택 모달로 채워 넣을 대상 item의 uuid
    const [quickFillTargetUuid, setQuickFillTargetUuid] = useState(null)
    // 상품에서 추가: 셀러툴 상품 목록에서 클릭으로 새 항목을 추가하는 모달
    const [addFromProductModalOpen, setAddFromProductModalOpen] = useState(false)
    // 수량 키패드: { uuid, field, label, value } 또는 null
    const [quantityKeypad, setQuantityKeypad] = useState(null)

    const handleOpenQuantityKeypad = (uuid, field, label, value) => {
        setQuantityKeypad({ uuid, field, label, value: Number(value) || 0 })
    }

    const handleCloseQuantityKeypad = () => {
        setQuantityKeypad(null)
    }

    const handleConfirmQuantity = (newValue) => {
        if (!quantityKeypad) {
            return
        }

        const { uuid, field } = quantityKeypad

        setNewRackLocation((prev) => {
            return {
                ...prev,
                items: prev.items?.map((r) => {
                    if (r.uuid === uuid) {
                        return { ...r, [field]: newValue }
                    }
                    return { ...r }
                }),
            }
        })
    }

    const handleOpenAddFromProductModal = () => {
        setAddFromProductModalOpen(true)
    }

    const handleCloseAddFromProductModal = () => {
        setAddFromProductModalOpen(false)
    }

    const handleAddItemFromProductOption = (productOption) => {
        setNewRackLocation((prev) => {
            return {
                ...prev,
                items: prev.items.concat({
                    uuid: uuidv4(),
                    productName: productOption?.productName || "",
                    optionName: productOption?.productOptionName || "",
                    optionCode: productOption?.productOptionCode || "",
                    productQuantity: 0,
                    boxQuantity: 0,
                }),
            }
        })
        customToast.success(`추가됨: ${productOption?.productName || ""}`)
    }

    const handleCopyItem = (item) => {
        setNewRackLocation((prev) => {
            return {
                ...prev,
                items: prev.items.concat({ ...item, uuid: uuidv4() }),
            }
        })
    }

    const handleOpenQuickFill = (uuid) => {
        setQuickFillTargetUuid(uuid)
    }

    const handleCloseQuickFill = () => {
        setQuickFillTargetUuid(null)
    }

    const handleSelectProductOption = (productOption) => {
        if (!quickFillTargetUuid) {
            return
        }

        setNewRackLocation((prev) => {
            return {
                ...prev,
                items: prev.items?.map((r) => {
                    if (r.uuid === quickFillTargetUuid) {
                        return {
                            ...r,
                            productName: productOption?.productName ?? r.productName,
                            optionName: productOption?.productOptionName ?? r.optionName,
                            optionCode: productOption?.productOptionCode ?? r.optionCode,
                        }
                    }
                    return { ...r }
                }),
            }
        })
    }

    const handleChangeType = () => {
        setNewRackLocation((prev) => {
            return { ...prev, type: prev?.type === "NORMAL" ? "INVENTORY" : "NORMAL" }
        })
    }

    const handleChangeAllowedSize = (e) => {
        const value = e.target.value
        const name = e.target.name

        const regex = /[1-9][0-9]*$/g

        if (!value) {
            setNewRackLocation((prev) => {
                return {
                    ...prev,
                    [name]: "",
                }
            })
            return
        }

        if (!regex.test(value)) {
            return
        }

        setNewRackLocation((prev) => {
            return {
                ...prev,
                [name]: Number(value),
            }
        })
    }

    const handleChangeTextareaForAdd = (e) => {
        setTextareaForAdd(e.target.value)
    }

    const handleAddWithTextarea = () => {
        const ALLOWED_UNITS = ["BOX", "EA"]

        const itemStringList = textareaForAdd.split("\n")
        const newAddedItems = []

        itemStringList.forEach((itemString, index) => {
            const itemFieldList = itemString.split(",")
            if (!itemFieldList[0]) {
                return
            }

            const productName = itemFieldList[0] ? itemFieldList[0]?.trim() : ""
            const optionName = itemFieldList[1] ? itemFieldList[1]?.trim() : ""
            const optionCode = itemFieldList[2] ? itemFieldList[2]?.trim() : ""
            const productQuantity = CustomNumberUtils().isInteger(itemFieldList[3]) ? Number(itemFieldList[3]) : 0
            const boxQuantity = CustomNumberUtils().isInteger(itemFieldList[4]) ? Number(itemFieldList[4]) : 0

            newAddedItems.push({
                uuid: uuidv4(),
                productName: productName,
                optionName: optionName,
                optionCode: optionCode,
                productQuantity: productQuantity,
                boxQuantity: boxQuantity,
            })
        })

        setNewRackLocation((prev) => {
            return {
                ...prev,
                items: prev.items.concat([...newAddedItems]),
            }
        })
    }

    const handleDeleteItem = (uuid) => {
        customToast.info("삭제됨.",{
            autoClose: 1000,
        })
        setNewRackLocation((prev) => {
            return {
                ...prev,
                items: prev.items?.filter((r) => r.uuid !== uuid),
            }
        })
    }

    const handleChangeTextValue = (e, uuid) => {
        const name = e.target.name
        const value = e.target.value

        setNewRackLocation((prev) => {
            return {
                ...prev,
                items: prev.items?.map((r) => {
                    if (r.uuid === uuid) {
                        return {
                            ...r,
                            [name]: value,
                        }
                    } else {
                        return { ...r }
                    }
                }),
            }
        })
    }

    const handleChangeUnit = (uuid) => {
        setNewRackLocation((prev) => {
            return {
                ...prev,
                items: prev.items?.map((r) => {
                    if (r.uuid === uuid) {
                        return {
                            ...r,
                            unit: r.unit === "BOX" ? "EA" : "BOX",
                        }
                    } else {
                        return { ...r }
                    }
                }),
            }
        })
    }

    const handleOpenLinkToLocationModal = () => {
        setLinkToLocationModalOpen(true)
    }

    const handleCloseLinkToLocationModal = () => {
        setLinkToLocationModalOpen(false)
    }

    const handleOpenDeleteModal = () => {
        setDeleteModalOpen(true)
    }

    const handleCloseDeleteModal = () => {
        setDeleteModalOpen(false)
    }

    const handleChangeLinkToLocationAddress = (newLinkToLocationAddress) => {
        if (newLinkToLocationAddress) {
            setNewRackLocation((prev) => {
                return {
                    ...prev,
                    linkToLocationAddress: newLinkToLocationAddress,
                }
            })
        } else {
            setNewRackLocation((prev) => {
                return {
                    ...prev,
                    linkToLocationAddress: "",
                }
            })
        }
        handleCloseLinkToLocationModal()
    }

    const handleSubmit = () => {
        const submittedNewRackLocation = { ...newRackLocation }
        if (submittedNewRackLocation?.linkToLocationAddress || submittedNewRackLocation?.items?.length > 0) {
            submittedNewRackLocation.enabledYn = "n"
        } else {
            submittedNewRackLocation.enabledYn = "y"
        }

        onSaveTargetRackLocation(submittedNewRackLocation)
        onClose()
    }

    const handleDeleteLocation = async () => {
        handleCloseDeleteModal()
        await onDeleteRackLocation()
    }

    return (
        <>
            <CustomDialog open={open} maxWidth="xl">
                <St.HeaderWrapper>
                    <CustomDialog.Header>
                        <CustomDialog.Header.Fake />
                        <CustomDialog.Header.Title>로케이션 넘버 {newRackLocation.address}</CustomDialog.Header.Title>
                        <CustomDialog.Header.Close onClick={() => onClose()} />
                    </CustomDialog.Header>
                </St.HeaderWrapper>
                <St.CompactInfo.Bar>
                    <St.CompactInfo.Field>
                        <span className="label">타입</span>
                        <St.CompactInfo.Toggle>
                            <button
                                type="button"
                                className={`normal ${newRackLocation.type === "NORMAL" ? "selected" : ""}`}
                                onClick={() => handleChangeType()}
                            >
                                NORMAL
                            </button>
                            <button
                                type="button"
                                className={`inventory ${newRackLocation.type === "INVENTORY" ? "selected" : ""}`}
                                onClick={() => handleChangeType()}
                            >
                                INVENTORY
                            </button>
                        </St.CompactInfo.Toggle>
                    </St.CompactInfo.Field>

                    <St.CompactInfo.Field>
                        <span className="label">링크</span>
                        <St.CompactInfo.LinkButton
                            type="button"
                            className={newRackLocation.linkToLocationAddress ? "selected" : ""}
                            onClick={() => handleOpenLinkToLocationModal()}
                        >
                            {newRackLocation.linkToLocationAddress ? newRackLocation.linkToLocationAddress : "선택"}
                        </St.CompactInfo.LinkButton>
                    </St.CompactInfo.Field>

                    <St.CompactInfo.Field>
                        <span className="label">규격</span>
                        <St.CompactInfo.SizeInputs>
                            <input
                                type="text"
                                name="allowedWidth"
                                placeholder="너비"
                                value={newRackLocation?.allowedWidth || ""}
                                onChange={(e) => handleChangeAllowedSize(e)}
                            />
                            <input
                                type="text"
                                name="allowedDepth"
                                placeholder="깊이"
                                value={newRackLocation?.allowedDepth || ""}
                                onChange={(e) => handleChangeAllowedSize(e)}
                            />
                            <input
                                type="text"
                                name="allowedHeight"
                                placeholder="높이"
                                value={newRackLocation?.allowedHeight || ""}
                                onChange={(e) => handleChangeAllowedSize(e)}
                            />
                        </St.CompactInfo.SizeInputs>
                    </St.CompactInfo.Field>
                </St.CompactInfo.Bar>
                <St.GenerateItem.Container>
                    <St.GenerateItem.Label>제품 추가</St.GenerateItem.Label>
                    <St.GenerateItem.Tabs>
                        <button
                            type="button"
                            className={addTab === "PRODUCT" ? "active" : ""}
                            onClick={() => setAddTab("PRODUCT")}
                        >
                            셀러툴 상품 불러오기
                        </button>
                        <button
                            type="button"
                            className={addTab === "TEXT" ? "active" : ""}
                            onClick={() => setAddTab("TEXT")}
                        >
                            텍스트 추가
                        </button>
                    </St.GenerateItem.Tabs>

                    {addTab === "PRODUCT" && (
                        <St.GenerateItem.TabPanel>
                            <div className="hint">셀러툴에 등록된 상품을 검색해 클릭으로 추가합니다.</div>
                            <St.GenerateItem.PickButton type="button" onClick={() => handleOpenAddFromProductModal()}>
                                셀러툴 상품 목록 열기
                            </St.GenerateItem.PickButton>
                        </St.GenerateItem.TabPanel>
                    )}

                    {addTab === "TEXT" && (
                        <St.GenerateItem.TabPanel>
                            <St.GenerateItem.Textarea
                                value={textareaForAdd || ""}
                                onChange={handleChangeTextareaForAdd}
                                placeholder={`{상품명},{옵션명},{옵션코드},{제품수량},{박스수량}\n{상품명},{옵션명},{옵션코드},{제품수량},{박스수량}\n...\n형식:string,string,string,number,number`}
                            ></St.GenerateItem.Textarea>
                            <St.GenerateItem.AddButton type="button" onClick={() => handleAddWithTextarea()}>
                                텍스트로 추가
                            </St.GenerateItem.AddButton>
                        </St.GenerateItem.TabPanel>
                    )}
                </St.GenerateItem.Container>
                <div style={{padding: '10px 20px', display: 'flex', gap: '10px', fontSize:'14px'}}>
                    <div>현재: {targetRackLocation?.items?.length}</div>
                    <div>수정후: <span style={{color:'var(--blueColor1)', fontWeight:'600'}}>{newRackLocation?.items?.length}</span></div>
                </div>
                <St.Items.Container>
                    <St.Items.TableWrapper>
                        <table>
                            <colgroup>
                                <col style={{ width: "70px" }} />
                                <col style={{ width: "70px" }} />
                                <col style={{ width: "80px" }} />
                                <col style={{ width: "150px" }} />
                                <col style={{ width: "150px" }} />
                                <col style={{ width: "150px" }} />
                                <col style={{ width: "70px" }} />
                                <col style={{ width: "70px" }} />
                            </colgroup>
                            <thead>
                                <tr>
                                    <th>삭제</th>
                                    <th>복사</th>
                                    <th>빠른채움</th>
                                    <th>상품명</th>
                                    <th>옵션명</th>
                                    <th>옵션코드</th>
                                    <th>제품수량</th>
                                    <th>박스수량</th>
                                </tr>
                            </thead>
                            <tbody>
                                {newRackLocation?.items?.map((item) => {
                                    return (
                                        <tr key={item?.uuid}>
                                            <td>
                                                <CustomBlockButton
                                                    type="button"
                                                    className="deleteButton"
                                                    onClick={() => handleDeleteItem(item.uuid)}
                                                >
                                                    삭제
                                                </CustomBlockButton>
                                            </td>
                                            <td>
                                                <CustomBlockButton
                                                    type="button"
                                                    className="copyButton"
                                                    onClick={() => handleCopyItem(item)}
                                                >
                                                    복사
                                                </CustomBlockButton>
                                            </td>
                                            <td>
                                                <CustomBlockButton
                                                    type="button"
                                                    className="quickFillButton"
                                                    onClick={() => handleOpenQuickFill(item.uuid)}
                                                >
                                                    빠른채움
                                                </CustomBlockButton>
                                            </td>
                                            <td>
                                                <input
                                                    type="text"
                                                    name="productName"
                                                    value={item.productName || ""}
                                                    onChange={(e) => handleChangeTextValue(e, item.uuid)}
                                                ></input>
                                            </td>
                                            <td>
                                                <input
                                                    type="text"
                                                    name="optionName"
                                                    value={item.optionName || ""}
                                                    onChange={(e) => handleChangeTextValue(e, item.uuid)}
                                                ></input>
                                            </td>
                                            <td>
                                                <input
                                                    type="text"
                                                    name="optionCode"
                                                    value={item.optionCode || ""}
                                                    onChange={(e) => handleChangeTextValue(e, item.uuid)}
                                                ></input>
                                            </td>
                                            <td>
                                                <button
                                                    type="button"
                                                    className="quantityCell"
                                                    onClick={() => handleOpenQuantityKeypad(item.uuid, "productQuantity", "제품수량", item.productQuantity)}
                                                >
                                                    {item.productQuantity || 0}
                                                </button>
                                            </td>
                                            <td>
                                                <button
                                                    type="button"
                                                    className="quantityCell"
                                                    onClick={() => handleOpenQuantityKeypad(item.uuid, "boxQuantity", "박스수량", item.boxQuantity)}
                                                >
                                                    {item.boxQuantity || 0}
                                                </button>
                                            </td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </table>
                    </St.Items.TableWrapper>
                </St.Items.Container>
                <St.Notice.Container>
                    <div className="notice">
                        <span>[링크 투 로케이션]</span>이 설정 되었거나, <span>[하위 제품들]</span>이 있을 경우{" "}
                        <span>"입고 랙 사용여부"</span>를 <span className="red">"사용불가"</span>로 처리 됩니다.
                    </div>
                </St.Notice.Container>
                <St.DangerZone>
                    <button type="button" onClick={() => handleOpenDeleteModal()}>
                        이 로케이션 삭제
                    </button>
                </St.DangerZone>
                <St.Footer.Container>
                    <CustomBlockButton type="button" onClick={() => handleSubmit()}>
                        저장
                    </CustomBlockButton>
                </St.Footer.Container>

                {/* 키패드는 설정 다이얼로그의 포커스 트랩 안에 있어야 직접 입력이 가능하므로 다이얼로그 내부에 렌더 */}
                <CustomQuantityKeypadModal
                    open={!!quantityKeypad}
                    label={quantityKeypad?.label}
                    initialValue={quantityKeypad?.value}
                    onClose={handleCloseQuantityKeypad}
                    onConfirm={handleConfirmQuantity}
                />
            </CustomDialog>

            {linkToLocationModalOpen && (
                <CustomDialog open={linkToLocationModalOpen} onClose={() => handleCloseLinkToLocationModal()}>
                    <St.LinkToLocationModal.Container>
                        <div className="headerWrapper">
                            <CustomDialog.Header>
                                <CustomDialog.Header.Fake />
                                <CustomDialog.Header.Title>링크 투 로케이션 선택</CustomDialog.Header.Title>
                                <CustomDialog.Header.Close onClick={() => handleCloseLinkToLocationModal()} />
                            </CustomDialog.Header>
                        </div>
                        <div className="bodyWrapper">
                            <CustomBlockButton type="button" onClick={() => handleChangeLinkToLocationAddress("")}>
                                선택 안함
                            </CustomBlockButton>
                            {rackLocations
                                ?.filter((r) => (r.address !== targetRackLocation?.address && r?.address?.includes(targetRackLocation?.address?.substring(0, 8))))
                                ?.map((rackLocation) => {
                                    return (
                                        <CustomBlockButton
                                            key={rackLocation.uuid}
                                            type="button"
                                            onClick={() => handleChangeLinkToLocationAddress(rackLocation.address)}
                                        >
                                            {rackLocation?.address}
                                        </CustomBlockButton>
                                    )
                                })}
                        </div>
                    </St.LinkToLocationModal.Container>
                </CustomDialog>
            )}

            {deleteModalOpen && (
                <CustomDialog open={deleteModalOpen} onClose={() => handleCloseDeleteModal()}>
                    <St.DeleteModal.Container>
                        <St.DeleteModal.HeaderWrapper>
                            <CustomDialog.Header>
                                <CustomDialog.Header.Fake />
                                <CustomDialog.Header.Title>로케이션 삭제</CustomDialog.Header.Title>
                                <CustomDialog.Header.Close onClick={() => handleCloseDeleteModal()} />
                            </CustomDialog.Header>
                        </St.DeleteModal.HeaderWrapper>
                    </St.DeleteModal.Container>
                    <St.DeleteModal.BodyWrapper>
                        <div className="notice">로케이션을 삭제하시겠습니까?</div>
                        <div className="buttons">
                            <CustomBlockButton type="button" className="cancel" onClick={() => handleCloseDeleteModal()}>
                                취소
                            </CustomBlockButton>
                            <CustomBlockButton type="button" className="delete" onClick={() => handleDeleteLocation()}>
                                삭제
                            </CustomBlockButton>
                        </div>
                    </St.DeleteModal.BodyWrapper>
                </CustomDialog>
            )}

            <ProductOptionPickerModal
                open={!!quickFillTargetUuid}
                onClose={handleCloseQuickFill}
                onSelect={handleSelectProductOption}
            />

            <ProductOptionPickerModal
                open={addFromProductModalOpen}
                onClose={handleCloseAddFromProductModal}
                onSelect={handleAddItemFromProductOption}
                closeOnSelect={false}
                title='셀러툴 상품에서 추가'
            />
        </>
    )
}
